import { InformationCircleIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'

interface NistDisclaimerProps {
  /** Whether to show as collapsible (default: false shows full content) */
  collapsible?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * NIST SP 800-53 disclaimer component explaining the scope of Guardian's coverage.
 *
 * Key points:
 * - Guardian covers automated technical controls via Microsoft Graph API
 * - NIST SP 800-53 Rev 5 has 1,000+ controls including organizational/physical
 * - Microsoft Purview Compliance Manager provides comprehensive coverage
 * - Guardian complements Purview with continuous automated monitoring
 */
export default function NistDisclaimer({ className = '' }: NistDisclaimerProps) {
  return (
    <div className={`bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 ${className}`}>
      <div className="flex gap-3">
        <div className="p-2 rounded-lg bg-blue-500/15 flex-shrink-0 h-fit">
          <InformationCircleIcon className="h-5 w-5 text-blue-400" />
        </div>
        <div className="flex-1 min-w-0 space-y-3">
          <div>
            <h4 className="text-sm font-medium text-slate-200 mb-1">
              About NIST SP 800-53 Coverage
            </h4>
            <p className="text-sm text-slate-400 leading-relaxed">
              Guardian's NIST SP 800-53 coverage focuses on{' '}
              <span className="text-blue-400 font-medium">automated technical controls</span>{' '}
              that can be evaluated via Microsoft Graph API. NIST SP 800-53 Rev 5 contains
              over 1,000 controls spanning technical, operational, and management
              categories—many of which require organizational processes, documentation, or
              physical controls that cannot be assessed programmatically.
            </p>
          </div>

          <div className="bg-dark-800/50 rounded-lg p-3">
            <p className="text-sm text-slate-300 mb-2">
              For comprehensive NIST 800-53 compliance assessment, we recommend:
            </p>
            <a
              href="https://compliance.microsoft.com/compliancemanager"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors"
            >
              <span>Microsoft Purview Compliance Manager</span>
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </a>
            <ul className="mt-2 space-y-1 text-xs text-slate-500">
              <li className="flex items-start gap-2">
                <span className="text-slate-600 mt-0.5">-</span>
                <span>Built-in NIST SP 800-53 assessment templates</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-600 mt-0.5">-</span>
                <span>Tracks both Microsoft-managed and customer-managed controls</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-600 mt-0.5">-</span>
                <span>Includes organizational controls that require manual attestation</span>
              </li>
            </ul>
          </div>

          <p className="text-xs text-slate-500 leading-relaxed">
            Guardian complements Purview by providing{' '}
            <span className="text-slate-400">continuous automated monitoring</span>{' '}
            of technical security configurations, while Purview provides the full compliance
            picture including policies, procedures, and attestations.
          </p>
        </div>
      </div>
    </div>
  )
}
